import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import Chart from 'chart.js/auto';

// Define Finance fraud metrics with keys, display names, values, and detailed hypothetical data.
const metrics = [
  {
    key: "expenseAnomalies",
    name: "Expense Report Anomalies",
    value: "15",
    details: "Expense Report Anomalies: In the last quarter, 15 unusual expense report entries were detected, indicating potential misreporting or fraudulent claims. Enhanced review processes and tighter expense policies are recommended. Disclaimer: The data provided is hypothetical and for demonstration purposes only."
  },
  {
    key: "revenueRecognition",
    name: "Revenue Recognition Issues",
    value: "7",
    details: "Revenue Recognition Issues: There were 7 instances of revenue recognition discrepancies, which may affect financial reporting accuracy. Regular audits and improved revenue process controls are advised. Disclaimer: The data provided is hypothetical and for demonstration purposes only."
  },
  {
    key: "paymentDiscrepancies",
    name: "Payment Discrepancies",
    value: "9",
    details: "Payment Discrepancies: Nine payment discrepancies were identified, suggesting possible unauthorized or erroneous disbursements. Implementation of automated reconciliation is recommended. Disclaimer: The data provided is hypothetical and for demonstration purposes only."
  }
];

// Helper function to generate dummy instance data for a given finance metric.
const generateInstances = (key, count, metricName) => {
  const prefixes = {
    expenseAnomalies: "EXP",
    revenueRecognition: "REV",
    paymentDiscrepancies: "PAY"
  };
  const prefix = prefixes[key] || "GEN";
  const statuses = ["Investigating", "Resolved", "Pending", "Under Review"];
  const instances = [];
  for (let i = 1; i <= count; i++) {
    instances.push({
      id: `${prefix}${String(i).padStart(3, '0')}`,
      indicator: metricName,
      description: `Instance ${i} for ${metricName}.`,
      date: "2025-03-10",
      status: statuses[i % statuses.length]
    });
  }
  return instances;
};

const getInstancesForIndicator = (selectedIndicator) => {
  let instances = [];
  if (selectedIndicator === "all") {
    metrics.forEach(metric => {
      const count = parseFloat(metric.value) || 0;
      const generated = generateInstances(metric.key, Math.floor(count), metric.name);
      instances = instances.concat(generated);
    });
  } else {
    const metric = metrics.find(m => m.key === selectedIndicator);
    const count = parseFloat(metric.value) || 0;
    instances = generateInstances(metric.key, Math.floor(count), metric.name);
  }
  return instances;
};

const MiniCards = ({ selectedIndicator, onCardClick }) => {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 mb-8">
      {metrics.map((metric) => (
        <div 
          key={metric.key}
          onClick={() => onCardClick(metric.key)}
          className={`p-4 bg-white shadow rounded cursor-pointer hover:bg-blue-50 transition ${
            selectedIndicator === metric.key ? "border-2 border-blue-600" : ""
          }`}
        >
          <h3 className="text-lg font-semibold text-gray-700">{metric.name}</h3>
          <p className="text-2xl font-bold text-blue-600">{metric.value}</p>
        </div>
      ))}
    </div>
  );
};

const FinanceProcessPage = () => {
  const chartRef = useRef(null);
  const [selectedIndicator, setSelectedIndicator] = useState("all");
  const [fraudChart, setFraudChart] = useState(null);

  // Current date for the "Last Updated" display.
  const currentDate = new Date().toLocaleDateString();

  useEffect(() => {
    // Destroy existing chart instance before creating a new one.
    if (fraudChart) {
      fraudChart.destroy();
    }

    const ctx = chartRef.current.getContext("2d");

    // Build chart labels and data based on the selected indicator.
    let labels = [];
    let data = [];
    if (selectedIndicator === "all") {
      labels = metrics.map(metric => metric.name);
      data = metrics.map(metric => {
        const numericValue = parseFloat(metric.value);
        return isNaN(numericValue) ? 0 : numericValue;
      });
    } else {
      const metric = metrics.find(m => m.key === selectedIndicator);
      labels = [metric.name];
      const numericValue = parseFloat(metric.value);
      data = [isNaN(numericValue) ? 0 : numericValue];
    }

    const newChart = new Chart(ctx, {
      type: "bar",
      data: {
        labels: labels,
        datasets: [{
          label: "Fraud Risk Score",
          data: data,
          backgroundColor: [
            "rgba(255, 99, 132, 0.6)",
            "rgba(54, 162, 235, 0.6)",
            "rgba(255, 206, 86, 0.6)"
          ],
          borderColor: [
            "rgba(255, 99, 132, 1)",
            "rgba(54, 162, 235, 1)",
            "rgba(255, 206, 86, 1)"
          ],
          borderWidth: 1
        }]
      },
      options: {
        scales: {
          y: { beginAtZero: true }
        },
        plugins: {
          legend: {
            labels: {
              font: { size: 14 }
            }
          }
        }
      }
    });

    setFraudChart(newChart);

    // Cleanup: destroy chart on component unmount.
    return () => {
      newChart.destroy();
    };
  }, [selectedIndicator]);

  const handleCardClick = (indicatorKey) => {
    setSelectedIndicator(indicatorKey);
  };

  const getDetails = () => {
    if (selectedIndicator === "all") {
      return "Overall Finance Fraud Analysis: Aggregated data across all finance fraud indicators. Click on any card for detailed insights. Disclaimer: The data provided is hypothetical and for demonstration purposes only.";
    }
    const metric = metrics.find(m => m.key === selectedIndicator);
    return metric ? metric.details : "No details available. Disclaimer: The data provided is hypothetical and for demonstration purposes only.";
  };

  const instances = getInstancesForIndicator(selectedIndicator);

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col px-4 py-8">
      <header className="mb-6">
        <div className="flex flex-col md:flex-row md:justify-between md:items-center">
          <h1 className="text-3xl font-bold text-blue-700">
            Finance Department - Fraud Indicators
          </h1>
          <p className="text-gray-600 mt-2 md:mt-0">Last Updated: {currentDate}</p>
        </div>
        <p className="mt-2 text-gray-700">
          Global Fashion &amp; Retail Inc., a $100 million revenue enterprise in the fashion and retail industry, employs a continuous audit monitoring system to ensure internal controls work effectively. Select an indicator to review processes and detailed fraud indicators.
        </p>
      </header>
      <nav className="mb-4">
        <Link 
          to="/departments" 
          className="text-blue-500 underline hover:text-blue-600"
        >
          Back to Departments
        </Link>
      </nav>
      {/* Clickable Mini Cards */}
      <MiniCards selectedIndicator={selectedIndicator} onCardClick={handleCardClick} />
      <section className="flex flex-col items-center">
        {/* Filter Dropdown as an alternative control */}
        <div className="mb-6">
          <label htmlFor="indicatorFilter" className="mr-2 font-medium">
            Filter Indicator:
          </label>
          <select 
            id="indicatorFilter" 
            value={selectedIndicator} 
            onChange={(e) => setSelectedIndicator(e.target.value)} 
            className="px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All</option>
            {metrics.map(metric => (
              <option key={metric.key} value={metric.key}>{metric.name}</option>
            ))}
          </select>
        </div>
        {/* Chart Container */}
        <div className="w-full max-w-2xl bg-white p-6 rounded shadow">
          <canvas ref={chartRef} width="400" height="200"></canvas>
        </div>
        {/* Details Section */}
        <div className="w-full max-w-2xl bg-white p-6 rounded shadow mt-6">
          <h2 className="text-2xl font-semibold text-gray-700 mb-4">Details</h2>
          <p className="text-gray-600">{getDetails()}</p>
        </div>
        {/* Instances Table Section */}
        <div className="w-full max-w-2xl bg-white p-6 rounded shadow mt-6 overflow-x-auto">
          <h2 className="text-2xl font-semibold text-gray-700 mb-4">Instances Identified</h2>
          <table className="min-w-full border-collapse">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Instance ID</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Indicator</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              </tr>
            </thead>
            <tbody className="bg-white">
              {instances.map(instance => (
                <tr key={instance.id} className="border-t">
                  <td className="px-4 py-2 text-sm text-gray-900">{instance.id}</td>
                  <td className="px-4 py-2 text-sm text-gray-900">{instance.indicator}</td>
                  <td className="px-4 py-2 text-sm text-gray-900">{instance.description}</td>
                  <td className="px-4 py-2 text-sm text-gray-900">{instance.date}</td>
                  <td className="px-4 py-2 text-sm text-gray-900">{instance.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
};

export default FinanceProcessPage;
